import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { useRouter } from 'src/hooks/use-router';
import { emailSender } from 'src/libs/email-sender';
import { trackMetaEvent } from 'src/libs/analytics/meta';
import { paths } from 'src/paths';
import { RouterLink } from 'src/components/router-link';
import { btn, fieldSx } from 'src/components/ctmass-ui';
import { BRAND } from 'src/theme/ctmass-tokens';
import { tr } from 'src/locales/tr';

export const ContactForm = () => {
    const router = useRouter();

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            message: ''
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().trim().required(tr("Enter your name.")),
            email: Yup.string().trim().email(tr("Enter a valid email address.")).required(tr("Enter your email.")),
            message: Yup.string().trim().required(tr("Write a short message."))
        }),
        onSubmit: async (values, helpers) => {
            try {
                await emailSender.sendFeedback(values.name, values.email, values.message);
                trackMetaEvent('Contact', { content_category: 'contact_form' }, { name: values.name, email: values.email });
                helpers.setStatus({ success: true });
                toast.success(tr("Message sent. We will reply by email."));
                router.replace(paths.index);
            } catch (error) {
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: error.message });
                toast.error("We couldn't send your message. Please try again or email support@ctmass.com.");
            } finally {
                helpers.setSubmitting(false);
            }
        }
    });

    const fieldProps = (name) => ({
        name,
        value: formik.values[name],
        onBlur: formik.handleBlur,
        onChange: formik.handleChange,
        error: !!(formik.touched[name] && formik.errors[name]),
        helperText: formik.touched[name] && formik.errors[name],
        fullWidth: true,
        variant: 'outlined',
        sx: fieldSx
    });

    return (
        <form noValidate onSubmit={formik.handleSubmit}>
            <Stack spacing={2.5}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2.5
                    }}
                >
                    <TextField label={tr("Full name")} autoComplete="name" required {...fieldProps('name')} />
                    <TextField label={tr("Email")} type="email" autoComplete="email" required {...fieldProps('email')} />
                </Box>
                <TextField
                    label={tr("Message")}
                    placeholder={tr("Tell us what you need help with, or what we could do better.")}
                    multiline
                    minRows={6}
                    required
                    {...fieldProps('message')}
                />
            </Stack>
            <Button
                fullWidth
                type="submit"
                disabled={formik.isSubmitting}
                endIcon={<SendRoundedIcon />}
                sx={{ ...btn.green, mt: 3, minHeight: 54, fontSize: 16 }}
            >
                {formik.isSubmitting ? tr("Sending...") : tr("Send message")}
            </Button>
            <Typography sx={{ mt: 2, fontSize: 13, lineHeight: 1.6, color: BRAND.muted }}>
                {tr("By sending this form, you agree to the")}{' '}
                <Link component={RouterLink} href={paths.privacyPolicy} sx={{ color: BRAND.navy, fontWeight: 600 }}>
                    {tr("Privacy Policy")}
                </Link>
                {' '}{tr("and")}{' '}
                <Link component={RouterLink} href={paths.cookiePolicy} sx={{ color: BRAND.navy, fontWeight: 600 }}>
                    {tr("Cookie Policy")}
                </Link>
                .
            </Typography>
        </form>
    );
};
